import {useMemo} from 'react';
import {Line} from 'react-chartjs-2';
import moment from 'moment';
import {
  averageNumber,
  baseOptions,
  comparisonBase,
  fillCalendarDays,
  plainNumber,
  seriesColor,
  seriesWash,
  summariseSeries,
} from './chartTheme';
import {ChartFrame, EmptyState, StatCard, StatRow} from './DashboardUI';
import styles from './dashboard.module.scss';

/**
 * A month of daily counts, with the figures that make the shape mean something.
 *
 * The chart it replaces had two problems worth naming.
 *
 * First, it plotted the API's response verbatim. That response contains only
 * the days something happened — for August it returned the 1st, 3rd, 5th, then
 * jumped to the 12th. Drawn on a categorical axis those points are evenly
 * spaced, so a week with no signups became a flat line holding at one per day.
 * `fillCalendarDays` puts the missing days back in at zero so horizontal
 * distance means elapsed time again, and a quiet week looks quiet.
 *
 * Second, it showed a curve and nothing else. Nobody opens a growth chart to
 * learn the shape; they open it to learn the number. The summary row carries
 * the total, the best day and the comparison against the month before, which is
 * the question the chart was being squinted at to answer.
 */

const monthLabel = (month, year) =>
  moment(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY');

export const GrowthChart = ({
  points,
  month,
  year,
  seriesIndex = 0,
  label = 'Users',
  // The previous month's `{date, amount}` days, for the comparison.
  previousPoints,
  // The previous month is still on its way: say so, not "no comparison".
  previousLoading,
  loading,
}) => {
  const series = useMemo(
    () => fillCalendarDays(points, month, year),
    [points, month, year],
  );

  const summary = useMemo(() => summariseSeries(series), [series]);

  const chartData = useMemo(
    () => ({
      labels: series.map(point => point.label),
      datasets: [
        {
          label,
          data: series.map(point => point.amount),
          borderColor: seriesColor(seriesIndex),
          backgroundColor: seriesWash(seriesIndex),
          borderWidth: 2,
          // A month of dots is noise; the points appear on hover instead.
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: '#ffffff',
          pointHoverBorderColor: seriesColor(seriesIndex),
          pointHoverBorderWidth: 2,
          fill: true,
          // Straight segments. A spline through daily counts invents values
          // between the days and can dip below zero on the way.
          tension: 0,
        },
      ],
    }),
    [series, seriesIndex, label],
  );

  const options = useMemo(() => {
    const base = baseOptions({integer: true, valueLabel: label});
    return {
      ...base,
      scales: {
        ...base.scales,
        x: {
          ...base.scales.x,
          ticks: {...base.scales.x.ticks, maxTicksLimit: 12},
        },
        y: {
          ...base.scales.y,
          // A month where the best day is 3 should not be gridded in halves.
          suggestedMax: Math.max(4, summary.peak?.amount ?? 0),
        },
      },
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            title: items =>
              moment(series[items[0].dataIndex]?.date).format('dddd D MMMM'),
            label: context => ` ${label}: ${plainNumber(context.parsed.y)}`,
          },
        },
      },
    };
  }, [label, series, summary.peak]);

  const compared = useMemo(
    () => comparisonBase(previousPoints, month, year),
    [previousPoints, month, year],
  );
  const base = compared?.base;
  const change =
    base == null || base === 0 ? null : ((summary.total - base) / base) * 100;

  // The comparison names the month it compares against. "last month" was
  // ambiguous the moment someone stepped back through the archive — viewing
  // July, "this month" and "last month" both meant something else.
  const previousMonth = moment(
    `${year}-${String(month).padStart(2, '0')}-01`,
  ).subtract(1, 'month');
  const previousName = previousMonth.format('MMMM');
  const inProgress = Boolean(compared?.inProgress);
  // "by 1 September": the same point in the previous month.
  const byThen = inProgress
    ? `by ${Math.min(
        compared.throughDay,
        previousMonth.daysInMonth(),
      )} ${previousName}`
    : `in ${previousName}`;

  const changeHint = (() => {
    if (!compared) {
      return previousLoading
        ? `comparing with ${previousName}…`
        : 'no comparison available';
    }
    if (base === 0) {
      return inProgress && compared.monthTotal > 0
        ? `none ${byThen} · ${plainNumber(
            compared.monthTotal,
          )} in all of ${previousName}`
        : `nothing ${byThen}`;
    }
    const direction = change >= 0 ? 'up' : 'down';
    return `${direction} ${Math.abs(change).toFixed(0)}% on ${plainNumber(
      base,
    )} ${byThen}`;
  })();

  const soFar = inProgress ? ' so far' : '';
  const hasAny = summary.total > 0;

  return (
    <div className={styles.section}>
      <StatRow columns={4}>
        <StatCard
          label={`${label} added`}
          value={plainNumber(summary.total)}
          hint={changeHint}
          tone={change == null ? null : change >= 0 ? 'Good' : 'Bad'}
          loading={loading}
        />
        <StatCard
          label="Best day"
          value={summary.peak ? plainNumber(summary.peak.amount) : '—'}
          hint={
            summary.peak
              ? moment(summary.peak.date).format('D MMMM')
              : 'no activity yet'
          }
          loading={loading}
        />
        <StatCard
          label="Daily average"
          value={hasAny ? averageNumber(summary.average) : '—'}
          hint={`across ${summary.days} day${
            summary.days === 1 ? '' : 's'
          }${soFar}`}
          loading={loading}
        />
        <StatCard
          label="Days with activity"
          value={plainNumber(summary.activeDays)}
          hint={
            inProgress
              ? `of ${summary.days} day${summary.days === 1 ? '' : 's'} so far`
              : `of ${summary.days} — ${
                  summary.days
                    ? Math.round((summary.activeDays * 100) / summary.days)
                    : 0
                }% of the month`
          }
          loading={loading}
        />
      </StatRow>

      {!loading && !hasAny ? (
        <EmptyState
          title={`No ${label.toLowerCase()} ${
            inProgress ? 'yet ' : ''
          }in ${monthLabel(month, year)}`}
          description={
            inProgress
              ? `${monthLabel(month, year)} is ${summary.days} day${
                  summary.days === 1 ? '' : 's'
                } old.${
                  compared?.monthTotal
                    ? ` ${previousName} had ${plainNumber(
                        compared.monthTotal,
                      )} in all — step back a month to see them.`
                    : ''
                }`
              : 'Pick another month, or check back once there is activity to plot.'
          }
        />
      ) : (
        <div className={styles.card}>
          <ChartFrame height={320}>
            <Line data={chartData} options={options} />
          </ChartFrame>
        </div>
      )}
    </div>
  );
};

export default GrowthChart;
